import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaClock } from "react-icons/fa";
import { Card, Divider, Tooltip, message } from "antd";
import { useCohortById, useUpdateCohortStatus, useCohortSessions, useUpdateCohortName } from "../../api/CohortsAPI";
import { useCompetenciesCount } from "../../api/CompetenciesAPI";
import CohortCodePopup from "../../components/cohorts/CohortCodePopup";
import ParticipantsTable from "../../components/cohorts/ParticipantsTable";
import EditableName from "../../components/common/EditableName";

const ManageReport = () => {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  const { data: cohort, isLoading } = useCohortById(cohortId);
  const [showAccessCode, setShowAccessCode] = useState(false);
  
  // Fetch participants data here instead of in the ParticipantsTable component
  const {
    data: participants,
    isLoading: isLoadingParticipants,
    error: participantsError
  } = useCohortSessions(cohortId);

  // Fetch total competencies count
  const { data: totalCompetenciesInDb } = useCompetenciesCount();

  // Add the mutation hook for updating cohort status
  const updateCohortStatusMutation = useUpdateCohortStatus();
  const updateCohortNameMutation = useUpdateCohortName();

  // Function to handle status change
  const handleStatusChange = (newStatus) => {
    updateCohortStatusMutation.mutate(
      { cohortId, status: newStatus },
      {
        onSuccess: () => {
          message.success(
            `Cohort ${
              newStatus === "active" ? "started" : "ended"
            } successfully`
          );
        },
        onError: (error) => {
          message.error(`Failed to update cohort status: ${error.message}`);
        },
      }
    );
  };

  // Calculate stats for the dashboard
  const completedSessions = participants?.filter(p => p.status === 'completed')?.length || 0;
  const incompleteSessions = participants?.filter(p => p.status === 'in_progress')?.length || 0;
  const totalParticipants = participants?.length || 0;
  
  // Calculate average competency percentage based on total competencies in database
  let avgCompetencyPercentage = 0;
  if (participants && participants.length > 0 && totalCompetenciesInDb) {
    const totalPercentages = participants.reduce((sum, participant) => {
      const percentage = Number(((participant.completed_competencies / totalCompetenciesInDb) * 100).toFixed(3));
      return sum + percentage;
    }, 0);
    avgCompetencyPercentage = Number((totalPercentages / participants.length).toFixed(3));
  }

  return (
    <div className="h-full py-6 px-10 flex flex-col overflow-hidden">
      <div className="flex justify-between items-center">
        <h1 className="font-semibold text-2xl">Manage Cohort</h1>
        <button
          onClick={() => navigate("/cohorts")}
          className="bg-primary hover:bg-primaryLight text-white font-semibold text-xs py-2 px-6 flex gap-2 rounded items-center"
        >
          <FaChevronLeft />
          Back
        </button>
      </div>
      <div className="flex mt-4 gap-6 flex-1 overflow-hidden">
        <div className="w-3/5 flex flex-col">
          <div className="flex gap-2">
            <div className="bg-white rounded-lg shadow p-4 flex-1">
              <p className="text-xs text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
                Completed Sessions
              </p>
              <p className="text-2xl font-bold mt-1 text-primary">
                {cohort?.status === "not_started" ? "-" : completedSessions}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-4 flex-1">
              <p className="text-xs text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
                Incomplete Sessions
              </p>
              <p className="text-2xl font-bold mt-1 text-primary">
                {cohort?.status === "not_started" ? "-" : incompleteSessions}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-4 flex-[2]">
              <p className="text-xs text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
                Average % of Competencies Achieved
              </p>
              <p className="text-2xl font-bold mt-1 text-primary">
                {cohort?.status === "not_started" ? "-" : avgCompetencyPercentage}%
              </p>
            </div>
          </div>
          <Card className="shadow bg-white mt-6 rounded-lg flex-1 flex flex-col overflow-hidden">
            <h2 className="font-semibold text-base mb-4">Participants</h2>

            {cohort?.status === "not_started" ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                <div className="bg-gray-100 rounded-full p-4 mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  No Participants Yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Start the cohort to allow participants to join
                </p>
              </div>
            ) : (
              <div className="flex-1">
                <ParticipantsTable 
                  participants={participants} 
                  isLoading={isLoadingParticipants} 
                  error={participantsError} 
                  totalCompetenciesInDb={totalCompetenciesInDb}
                />
              </div>
            )}
          </Card>
        </div>

        <div className="flex-1">
          <Card className="shadow bg-white rounded-lg">
            <h2 className="font-semibold text-lg mb-2 flex items-center gap-2">
              {isLoading ? (
                "Loading..."
              ) : (
                <EditableName
                  value={cohort?.name}
                  fallback="Untitled Cohort"
                  title="Edit cohort name"
                  inputLabel="Cohort name"
                  buttonText="Rename"
                  buttonClassName="!text-primary hover:!text-primaryLight"
                  onSave={(name) =>
                    new Promise((resolve, reject) => {
                      updateCohortNameMutation.mutate(
                        { cohortId, name },
                        {
                          onSuccess: () => {
                            message.success("Cohort name updated");
                            resolve();
                          },
                          onError: (error) => {
                            message.error(error.response?.data?.error || "Failed to update cohort name");
                            reject(error);
                          },
                        }
                      );
                    })
                  }
                />
              )}
            </h2>
            <Divider className="my-2 bg-gray-600" />
            <div className="flex flex-col gap-1 mt-2">
              <p className="text-xs text-primary">
                <span className="font-semibold mr-2 text-gray-600">
                  Cohort ID:
                </span>
                {cohort?.id}
              </p>
              <p className="text-xs text-primary">
                <span className="font-semibold mr-2 text-gray-600">
                  Created At:
                </span>
                {new Date(cohort?.created_at).toLocaleDateString()}
              </p>
              <p className="text-xs text-primary">
                <span className="font-semibold mr-2 text-gray-600">
                  Participants:
                </span>
                {totalParticipants}
              </p>
            </div>
          </Card>
          <Card className="shadow bg-white rounded-lg mt-4">
            <h2 className="font-semibold text-base mb-2 flex justify-between items-center">
              Cohort Status
              <div className="flex items-center gap-2">
                {cohort?.status === "not_started" && (
                  <span className="text-gray-600 text-sm font-medium">
                    Not Started
                  </span>
                )}
                {cohort?.status === "active" && (
                  <>
                    <span className="text-primary text-sm font-medium">
                      Active
                    </span>
                  
                  </>
                )}
                {cohort?.status === "archived" && (
                  <span className="text-gray-600 text-sm font-medium">
                    Archived
                  </span>
                )}
              </div>
            </h2>
            <Divider className="my-2 bg-gray-600" />
            <div className="flex flex-col gap-1 mt-4">
              {cohort?.status === "active" && (
                <button
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-medium py-2 rounded mb-2 text-xs"
                  onClick={() => setShowAccessCode(true)}
                >
                  Display Access Code
                </button>
              )}

              {cohort?.status !== "archived" && (
                <button
                  className="w-full bg-primary hover:bg-primaryLight text-white font-medium py-2 rounded mb-2 text-xs"
                  onClick={() =>
                    handleStatusChange(
                      cohort?.status === "not_started" ? "active" : "archived"
                    )
                  }
                  disabled={updateCohortStatusMutation.isPending}
                >
                  {updateCohortStatusMutation.isPending
                    ? cohort?.status === "not_started"
                      ? "Starting..."
                      : "Ending..."
                    : cohort?.status === "not_started"
                    ? "Start Cohort"
                    : "End Cohort"}
                </button>
              )}

              {cohort?.status === "archived" ? (
                <button 
                  onClick={() => navigate(`/cohorts/${cohortId}/report`)}
                  className="w-full bg-primary hover:bg-primaryLight text-white font-medium py-2 rounded text-xs"
                >
                  View Cohort Report
                </button>
              ) : (
                <Tooltip title="Please end the cohort first" placement="bottom">
                  <button
                    disabled
                    className="w-full bg-gray-400 text-white font-medium py-2 rounded cursor-not-allowed text-xs"
                  >
                    View Cohort Report
                  </button>
                </Tooltip>
              )}
            </div>
          </Card>
        </div>
      </div>

      <CohortCodePopup
        open={showAccessCode}
        onClose={() => setShowAccessCode(false)}
        accessCode={cohort?.join_code || "000000"}
      />
    </div>
  );
};

export default ManageReport;
